import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { doc, getDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { auth, db } from "../firebase";
import { notify } from "../utils/notifications";

function formatDateTime(ts) {
  if (!ts) return "";
  const date = ts?.toDate ? ts.toDate() : new Date(ts);
  if (isNaN(date)) return "";
  return date.toLocaleDateString([], {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatTime(ts) {
  if (!ts) return "";
  const date = ts?.toDate ? ts.toDate() : new Date(ts);
  return isNaN(date)
    ? ""
    : date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: true });
}

function AppointmentDetailsPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [appointment, setAppointment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteReason, setDeleteReason] = useState("");
  const [cancelReason, setCancelReason] = useState("");
  const [rescheduleReason, setRescheduleReason] = useState("");
  const [paymentStatus, setPaymentStatus] = useState(""); // "paid" or "unpaid"
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const docRef = doc(db, "appointments", id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = { id: docSnap.id, ...docSnap.data() };
          setAppointment(data);
          setPaymentStatus(data.paymentStatus || "unpaid");
        }
      } catch (e) {
        console.warn("Failed to load appointment", e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const getStatusLabel = (status) => {
    if (!status) return "Confirmed";
    const s = status.toLowerCase();
    if (s.includes("confirm")) return "Confirmed";
    if (s.includes("pending")) return "Pending";
    if (s.includes("cancel")) return "Cancelled";
    if (s.includes("complete")) return "Completed";
    return status;
  };

  const status = getStatusLabel(appointment?.status);
  const isCancelled = status === "Cancelled";
  const isCompleted = status === "Completed";
  const isFinalStatus = isCancelled || isCompleted;

  const handleComplete = async () => {
    try {
      await updateDoc(doc(db, "appointments", id), {
        status: "Completed",
      });

      navigate("/appointments");
    } catch (e) {
      console.error("Failed to complete appointment", e);
    }
  };

  const handleCancel = async () => {
    if (!cancelReason.trim()) {
      alert("Please provide a reason for cancellation");
      return;
    }

    try {
      const currentUser = auth.currentUser;
        await updateDoc(doc(db, "appointments", id), {
          status: "Cancelled",
        cancelReason: cancelReason.trim(),
        cancelledAt: new Date(),
      });

      // Send notification
      if (currentUser) {
        await notify(currentUser.uid, {
          type: "appointment_cancelled",
          appointmentId: id,
          patientName: appointment.patientName || "Patient",
          title: "Appointment Cancelled",
          message: `Appointment with ${
            appointment.patientName || "patient"
          } has been cancelled. Reason: ${cancelReason.trim()}`,
        });
      }

      setShowCancelModal(false);
      setCancelReason("");
        navigate("/appointments");
      } catch (e) {
        console.error("Failed to cancel appointment", e);
      }
  };

  const handleReschedule = () => {
    if (!rescheduleReason.trim()) {
      alert("Please provide a reason for rescheduling");
      return;
    }

    // Navigate to add appointment page with pre-filled data for rescheduling
    const startDate = appointment.start?.toDate
      ? appointment.start.toDate()
      : new Date(appointment.start);
    const dateStr = startDate.toISOString().split("T")[0];
    const timeStr = startDate.toTimeString().slice(0, 5);

    navigate(
      `/appointments/new?edit=${id}&patientId=${
        appointment.patientId || ""
      }&date=${dateStr}&time=${timeStr}&type=${encodeURIComponent(
        appointment.reason || "Consultation"
      )}&notes=${encodeURIComponent(
        appointment.notes || ""
      )}&rescheduleReason=${encodeURIComponent(rescheduleReason.trim())}`
    );
  };

  const handlePaymentStatusChange = async (newStatus) => {
    try {
      const currentUser = auth.currentUser;
      await updateDoc(doc(db, "appointments", id), {
        paymentStatus: newStatus,
      });

      setPaymentStatus(newStatus);

      // Send notification
      if (currentUser) {
        await notify(currentUser.uid, {
          type: newStatus === "paid" ? "payment_received" : "payment_pending",
          appointmentId: id,
          patientName: appointment.patientName || "Patient",
          title: newStatus === "paid" ? "Payment Received" : "Payment Pending",
          message: `Payment status for appointment with ${
            appointment.patientName || "patient"
          }: ${newStatus === "paid" ? "Paid" : "Pending"}`,
        });
      }
    } catch (e) {
      console.error("Failed to update payment status", e);
    }
  };

  const handleEdit = () => {
    // Navigate to add appointment page with pre-filled data for editing
    const startDate = appointment.start?.toDate
      ? appointment.start.toDate()
      : new Date(appointment.start);
    const dateStr = startDate.toISOString().split("T")[0];
    const timeStr = startDate.toTimeString().slice(0, 5);

    navigate(
      `/appointments/new?edit=${id}&patientId=${
        appointment.patientId || ""
      }&date=${dateStr}&time=${timeStr}&type=${encodeURIComponent(
        appointment.reason || "Consultation"
      )}&notes=${encodeURIComponent(appointment.notes || "")}`
    );
  };

  const handleDelete = async () => {
    if (!deleteReason.trim()) {
      alert("Please provide a reason for deleting this appointment");
      return;
    }

    try {
      setDeleting(true);
      const currentUser = auth.currentUser;
      
      // Soft delete - mark as deleted instead of removing
      await updateDoc(doc(db, "appointments", id), {
        deleted: true,
        deletedAt: serverTimestamp(),
        deleteReason: deleteReason.trim(),
        deletedBy: currentUser?.uid || null,
      });

      // Send notification
      if (currentUser) {
        await notify(currentUser.uid, {
          type: "appointment_deleted",
          appointmentId: id,
          patientName: appointment.patientName || "Patient",
          title: "Appointment Deleted",
          message: `Appointment with ${
            appointment.patientName || "patient"
          } has been deleted. Reason: ${deleteReason.trim()}`,
        });
      }

      setShowDeleteModal(false);
      setDeleteReason("");
      navigate("/appointments");
    } catch (e) {
      console.error("Failed to delete appointment", e);
      alert("Failed to delete appointment. Please try again.");
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="appt-detail-layout">
        <header className="appt-detail-header">
          <button
            className="appt-detail-back-btn"
            onClick={() => navigate("/appointments")}
          >
            <span className="material-symbols-outlined">arrow_back</span>
          </button>
          <h1>Appointment Details</h1>
          <div className="appt-detail-header-spacer" />
        </header>
        <div className="appt-detail-content">
          <div
            className="appt-detail-card"
            style={{ textAlign: "center", padding: "48px 24px" }}
          >
            <p
              style={{
                color: "var(--text-muted)",
                fontSize: "15px",
                fontWeight: "600",
              }}
            >
              Loading appointment details...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!appointment) {
    return (
      <div className="appt-detail-layout">
        <header className="appt-detail-header">
          <button
            className="appt-detail-back-btn"
            onClick={() => navigate("/appointments")}
          >
            <span className="material-symbols-outlined">arrow_back</span>
          </button>
          <h1>Appointment Details</h1>
          <div className="appt-detail-header-spacer" />
        </header>
        <div className="appt-detail-content">
          <div
            className="appt-detail-card"
            style={{ textAlign: "center", padding: "48px 24px" }}
          >
            <span
              className="material-symbols-outlined"
              style={{
                fontSize: "48px",
                color: "var(--text-muted)",
                marginBottom: "16px",
                display: "block",
              }}
            >
              event_busy
            </span>
            <h3
              style={{
                margin: "0 0 8px",
                color: "var(--text-primary)",
                fontSize: "18px",
                fontWeight: "800",
              }}
            >
              Appointment not found
            </h3>
            <p
              style={{
                color: "var(--text-muted)",
                fontSize: "15px",
                fontWeight: "500",
                margin: 0,
              }}
            >
              The appointment you're looking for doesn't exist or has been
              removed.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const initialFromName = (name) => {
    if (!name) return "P";
    return name.trim().charAt(0).toUpperCase();
  };

  return (
    <div className="appt-detail-layout">
      <header className="appt-detail-header">
        <button
          className="appt-detail-back-btn"
          onClick={() => navigate("/appointments")}
        >
          <span className="material-symbols-outlined">arrow_back</span>
        </button>
        <h1>Appointment Details</h1>
        <div className="appt-detail-header-spacer" />
      </header>

      <div className="appt-detail-content">
        {/* Patient Info Card */}
        <div className="appt-detail-card appt-detail-patient-card">
          <div className="appt-detail-patient-header">
          {appointment.avatarUrl ? (
            <img
                className="appt-detail-avatar"
              src={appointment.avatarUrl}
              alt={appointment.patientName}
            />
          ) : (
              <div className="appt-detail-avatar-placeholder">
                <span>{initialFromName(appointment.patientName)}</span>
              </div>
            )}
            <div className="appt-detail-patient-info">
              <h2>{appointment.patientName || "Unknown Patient"}</h2>
              <div className="appt-detail-type-badge">
                <span className="material-symbols-outlined">
                  medical_services
                </span>
                <span>{appointment.reason || "Consultation"}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Status Card */}
        <div className="appt-detail-card appt-detail-status-card">
          <div className="appt-detail-status-header">
            <span className="material-symbols-outlined">info</span>
            <h3>Status</h3>
          </div>
          <div className="appt-detail-status-badge-wrapper">
            <span
              className={`appt-detail-status-badge appt-detail-status-${status.toLowerCase()}`}
            >
              <span className="material-symbols-outlined">
                {status === "Pending"
                  ? "schedule"
                  : status === "Completed"
                  ? "check_circle"
                  : status === "Cancelled"
                  ? "cancel"
                  : "check_circle"}
              </span>
              <span>{status}</span>
          </span>
          </div>
        </div>

        {/* Schedule Card */}
        <div className="appt-detail-card appt-detail-schedule-card">
          <div className="appt-detail-section-header">
            <span className="material-symbols-outlined">event</span>
            <h3>Schedule</h3>
          </div>
          <div className="appt-detail-schedule-grid">
            <div className="appt-detail-schedule-item">
              <div className="appt-detail-schedule-icon">
                <span className="material-symbols-outlined">
                  calendar_today
                </span>
              </div>
              <div className="appt-detail-schedule-content">
                <span className="appt-detail-schedule-label">Date</span>
                <span className="appt-detail-schedule-value">
                  {formatDateTime(appointment.start)}
                </span>
              </div>
            </div>
            <div className="appt-detail-schedule-item">
              <div className="appt-detail-schedule-icon">
                <span className="material-symbols-outlined">schedule</span>
              </div>
              <div className="appt-detail-schedule-content">
                <span className="appt-detail-schedule-label">Start Time</span>
                <span className="appt-detail-schedule-value">
                  {formatTime(appointment.start)}
                </span>
              </div>
            </div>
            <div className="appt-detail-schedule-item">
              <div className="appt-detail-schedule-icon">
                <span className="material-symbols-outlined">schedule</span>
              </div>
              <div className="appt-detail-schedule-content">
                <span className="appt-detail-schedule-label">End Time</span>
                <span className="appt-detail-schedule-value">
                  {(() => {
                    // Calculate end time: use stored end time if available, otherwise default to 30 minutes after start
                    let endTime = null;
                    if (appointment.end) {
                      endTime = appointment.end;
                    } else if (appointment.start) {
                      const startDate = appointment.start?.toDate
                        ? appointment.start.toDate()
                        : new Date(appointment.start);
                      // Default to 30 minutes after start if no end time is set
                      const defaultEnd = new Date(startDate.getTime() + 30 * 60000);
                      endTime = defaultEnd;
                    }
                    return endTime ? formatTime(endTime) : "N/A";
                  })()}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Payment Status Card */}
        {isCompleted && (
          <div className="appt-detail-card appt-detail-payment-card">
            <div className="appt-detail-section-header">
              <span className="material-symbols-outlined">payments</span>
              <h3>Payment Status</h3>
            </div>
            <div className="appt-detail-payment-content">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  marginBottom: "12px",
                }}
              >
                <span
                  className={`appt-detail-status-badge ${
                    paymentStatus === "paid"
                      ? "appt-detail-status-completed"
                      : "appt-detail-status-pending"
                  }`}
                >
                  <span className="material-symbols-outlined">
                    {paymentStatus === "paid" ? "check_circle" : "pending"}
                  </span>
                  <span>{paymentStatus === "paid" ? "Paid" : "Unpaid"}</span>
                </span>
              </div>
              <div style={{ display: "flex", gap: "8px" }}>
                <button
                  onClick={() => handlePaymentStatusChange("paid")}
                  disabled={paymentStatus === "paid"}
                  style={{
                    padding: "8px 16px",
                    background:
                      paymentStatus === "paid"
                        ? "var(--success)"
                        : "var(--primary)",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    fontSize: "14px",
                    fontWeight: "600",
                    cursor:
                      paymentStatus === "paid" ? "not-allowed" : "pointer",
                    opacity: paymentStatus === "paid" ? 0.7 : 1,
                  }}
                >
                  Mark as Paid
                </button>
                <button
                  onClick={() => handlePaymentStatusChange("unpaid")}
                  disabled={paymentStatus === "unpaid"}
                  style={{
                    padding: "8px 16px",
                    background:
                      paymentStatus === "unpaid"
                        ? "var(--error)"
                        : "transparent",
                    color:
                      paymentStatus === "unpaid"
                        ? "white"
                        : "var(--text-primary)",
                    border: "1px solid var(--border-color)",
                    borderRadius: "8px",
                    fontSize: "14px",
                    fontWeight: "600",
                    cursor:
                      paymentStatus === "unpaid" ? "not-allowed" : "pointer",
                    opacity: paymentStatus === "unpaid" ? 0.7 : 1,
                  }}
                >
                  Mark as Unpaid
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Notes Card */}
        <div className="appt-detail-card appt-detail-notes-card">
          <div className="appt-detail-section-header">
            <span className="material-symbols-outlined">notes</span>
            <h3>Notes</h3>
          </div>
          <div className="appt-detail-notes-content">
            {appointment.notes ? (
              <p>{appointment.notes}</p>
            ) : (
              <p className="appt-detail-notes-empty">
                No additional notes for this appointment.
              </p>
            )}
          </div>
        </div>

        {/* Cancel Reason Display */}
        {isCancelled && appointment.cancelReason && (
          <div className="appt-detail-card">
            <div className="appt-detail-section-header">
              <span className="material-symbols-outlined">info</span>
              <h3>Cancellation Reason</h3>
            </div>
            <div className="appt-detail-notes-content">
              <p>{appointment.cancelReason}</p>
            </div>
          </div>
        )}

        {/* Actions */}
        {!isFinalStatus && (
          <div className="appt-detail-actions-wrapper">
            <div className="appt-detail-secondary-actions">
              <button
                className="appt-detail-action-btn appt-detail-action-secondary"
                onClick={() => setShowRescheduleModal(true)}
              >
                <span className="material-symbols-outlined">event_repeat</span>
                <span>Reschedule</span>
              </button>
              <button
                className="appt-detail-action-btn appt-detail-action-secondary"
                onClick={handleEdit}
              >
                <span className="material-symbols-outlined">edit</span>
                <span>Edit Details</span>
              </button>
            </div>
            <div className="appt-detail-primary-actions">
              <button
                className="appt-detail-action-btn appt-detail-action-cancel"
                onClick={() => setShowCancelModal(true)}
              >
                <span className="material-symbols-outlined">close</span>
                <span>Cancel Appointment</span>
              </button>
              <button
                className="appt-detail-action-btn appt-detail-action-complete"
                onClick={handleComplete}
              >
                <span className="material-symbols-outlined">check_circle</span>
                <span>Mark Complete</span>
              </button>
            </div>
          </div>
        )}

        {isFinalStatus && (
          <div className="appt-detail-final-status">
            <div className="appt-detail-final-status-icon">
              <span className="material-symbols-outlined">
                {isCancelled ? "cancel" : "check_circle"}
              </span>
            </div>
            <h3>
              {isCancelled ? "Appointment Cancelled" : "Appointment Completed"}
            </h3>
            <p>
              {isCancelled
                ? "This appointment has been cancelled and cannot be modified."
                : "This appointment has been completed and cannot be modified."}
            </p>
          </div>
        )}

        {/* Delete Button - Available for all appointments */}
        <div className="appt-detail-actions-wrapper" style={{ marginTop: "16px", borderTop: "1px solid var(--border-color)", paddingTop: "16px" }}>
          <button
            className="appt-detail-action-btn"
            onClick={() => setShowDeleteModal(true)}
            style={{
              background: "#ef4444",
              color: "white",
              border: "none",
              width: "100%",
            }}
          >
            <span className="material-symbols-outlined">delete</span>
            <span>Delete Appointment</span>
          </button>
        </div>

        {/* Cancel Modal */}
        {showCancelModal && (
          <div
            className="settings-modal"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowCancelModal(false);
                setCancelReason("");
              }
            }}
          >
            <div
              className="settings-modal-card settings-form"
              onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: "500px" }}
            >
              <h3>Cancel Appointment</h3>
              <p
                style={{
                  fontSize: "14px",
                  color: "var(--text-muted)",
                  marginBottom: "20px",
                }}
              >
                Please provide a reason for cancelling this appointment with{" "}
                {appointment.patientName || "patient"}.
              </p>
              <div className="settings-field">
                <label>Reason for Cancellation *</label>
                <textarea
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="e.g., Patient requested, Emergency, etc."
                  rows={4}
                  style={{
                    width: "100%",
                    padding: "12px",
                    borderRadius: "8px",
                    border: "1px solid var(--border-color)",
                    fontSize: "15px",
                    background: "var(--bg-input)",
                    color: "var(--text-primary)",
                    resize: "vertical",
                  }}
                  autoFocus
                />
              </div>
              <div
                className="settings-modal-actions"
                style={{ marginTop: "20px" }}
              >
                <button
                  onClick={() => {
                    setShowCancelModal(false);
                    setCancelReason("");
                  }}
                  style={{
                    padding: "12px 24px",
                    background: "transparent",
                    color: "var(--text-primary)",
                    border: "1px solid var(--border-color)",
                    borderRadius: "8px",
                    fontSize: "15px",
                    fontWeight: "600",
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleCancel}
                  disabled={!cancelReason.trim()}
                  style={{
                    padding: "12px 24px",
                    background: "var(--error)",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    fontSize: "15px",
                    fontWeight: "600",
                    cursor: cancelReason.trim() ? "pointer" : "not-allowed",
                    opacity: cancelReason.trim() ? 1 : 0.5,
                  }}
                >
                  Confirm Cancellation
                </button>
              </div>
            </div>
        </div>
        )}

        {/* Reschedule Modal */}
        {showRescheduleModal && (
          <div
            className="settings-modal"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowRescheduleModal(false);
                setRescheduleReason("");
              }
            }}
          >
            <div
              className="settings-modal-card settings-form"
              onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: "500px" }}
            >
              <h3>Reschedule Appointment</h3>
              <p
                style={{
                  fontSize: "14px",
                  color: "var(--text-muted)",
                  marginBottom: "20px",
                }}
              >
                Please provide a reason for rescheduling this appointment with{" "}
                {appointment.patientName || "patient"}.
              </p>
              <div className="settings-field">
                <label>Reason for Rescheduling *</label>
                <textarea
                  value={rescheduleReason}
                  onChange={(e) => setRescheduleReason(e.target.value)}
                  placeholder="e.g., Patient requested different time, Conflict, etc."
                  rows={4}
                  style={{
                    width: "100%",
                    padding: "12px",
                    borderRadius: "8px",
                    border: "1px solid var(--border-color)",
                    fontSize: "15px",
                    background: "var(--bg-input)",
                    color: "var(--text-primary)",
                    resize: "vertical",
                  }}
                  autoFocus
                />
              </div>
              <div
                className="settings-modal-actions"
                style={{ marginTop: "20px" }}
              >
                <button
                  onClick={() => {
                    setShowRescheduleModal(false);
                    setRescheduleReason("");
                  }}
                  style={{
                    padding: "12px 24px",
                    background: "transparent",
                    color: "var(--text-primary)",
                    border: "1px solid var(--border-color)",
                    borderRadius: "8px",
                    fontSize: "15px",
                    fontWeight: "600",
                    cursor: "pointer",
                  }}
                >
            Cancel
          </button>
                <button
                  onClick={handleReschedule}
                  disabled={!rescheduleReason.trim()}
                  style={{
                    padding: "12px 24px",
                    background: "var(--primary)",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    fontSize: "15px",
                    fontWeight: "600",
                    cursor: rescheduleReason.trim() ? "pointer" : "not-allowed",
                    opacity: rescheduleReason.trim() ? 1 : 0.5,
                  }}
                >
                  Continue to Reschedule
          </button>
        </div>
            </div>
          </div>
        )}

        {/* Delete Modal */}
        {showDeleteModal && (
          <div
            className="settings-modal"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowDeleteModal(false);
                setDeleteReason("");
              }
            }}
          >
            <div
              className="settings-modal-card settings-form"
              onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: "500px" }}
            >
              <h3>Delete Appointment</h3>
              <p
                style={{
                  fontSize: "14px",
                  color: "var(--text-muted)",
                  marginBottom: "20px",
                }}
              >
                Please provide a reason for deleting this appointment with{" "}
                {appointment.patientName || "patient"}. This action cannot be undone.
              </p>
              <div className="settings-field">
                <label>Reason for Deletion *</label>
                <textarea
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                  placeholder="e.g., Duplicate entry, Incorrect information, etc."
                  rows={4}
                  style={{
                    width: "100%",
                    padding: "12px",
                    borderRadius: "8px",
                    border: "1px solid var(--border-color)",
                    fontSize: "15px",
                    background: "var(--bg-input)",
                    color: "var(--text-primary)",
                    resize: "vertical",
                  }}
                  autoFocus
                />
              </div>
              <div
                className="settings-modal-actions"
                style={{ marginTop: "20px" }}
              >
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeleteReason("");
                  }}
                  disabled={deleting}
                  style={{
                    padding: "12px 24px",
                    background: "transparent",
                    color: "var(--text-primary)",
                    border: "1px solid var(--border-color)",
                    borderRadius: "8px",
                    fontSize: "15px",
                    fontWeight: "600",
                    cursor: deleting ? "not-allowed" : "pointer",
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={!deleteReason.trim() || deleting}
                  style={{
                    padding: "12px 24px",
                    background: "#ef4444",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    fontSize: "15px",
                    fontWeight: "600",
                    cursor: deleteReason.trim() && !deleting ? "pointer" : "not-allowed",
                    opacity: deleteReason.trim() && !deleting ? 1 : 0.5,
                  }}
                >
                  {deleting ? "Deleting..." : "Delete Appointment"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AppointmentDetailsPage;
